import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import { AbstractService } from '@shared/ui/services/abstract.service';

export abstract class AbstractComponentStore<T extends object> extends AbstractService {
  protected state$: BehaviorSubject<T>;

  private behaviorSubjectsMap: Map<keyof T, BehaviorSubject<unknown>> = new Map();
  private observablesMap: Map<keyof T, Observable<unknown>> = new Map();

  constructor(initialState: T) {
    super();
    this.state$ = new BehaviorSubject<T>(initialState);
    const keys = Object.keys(initialState) as Array<keyof T>;
    keys.forEach((key: keyof T) => {
      const behaviourSubject = new BehaviorSubject<unknown>(initialState[key]);
      this.behaviorSubjectsMap.set(key, behaviourSubject);
      const observable: Observable<T[keyof T]> = this.selectWithProjector((state: T) => state[key]).pipe(
        takeUntil(this.destroyed$)
      );
      observable.subscribe((value: T[keyof T]) => {
        behaviourSubject.next(value);
      });
      this.observablesMap.set(key, observable);
    });
  }

  protected select<K extends keyof T>(key: K): BehaviorSubject<T[K]> {
    return this.behaviorSubjectsMap.get(key) as BehaviorSubject<T[K]>;
  }

  protected mapValue<R, K extends keyof T, V extends Array<T[K]>>(keys: K | K[], mapFn: (...values: V) => R): BehaviorSubject<R> {
    const keysArray: K[] = Array.isArray(keys) ? keys : [keys];
    const initialValues: Array<T[K]> = keysArray.map((key: K) => this.state$.value[key]);
    const initialValue: R = mapFn(...initialValues as V);
    const behaviourSubject = new BehaviorSubject<R>(initialValue);
    keysArray.forEach((key: K) => {
      const observable = this.observablesMap.get(key) as Observable<T[K]>;
      observable.subscribe(() => {
        const currentValues: Array<T[K]> = keysArray.map(k => this.state$.value[k]);
        behaviourSubject.next(mapFn(...currentValues as V));
      });
    });
    return behaviourSubject;
  }

  protected selectWithProjector<R>(projector: (state: T) => R): Observable<R> {
    return this.state$.asObservable().pipe(
      map(projector),
      distinctUntilChanged()
    );
  }

  protected updater<ValueType = void>(
    updaterFn: (state: T, value: ValueType) => T
  ): (value: ValueType) => void {
    return ((value: ValueType): void => {
      const currentState: T = this.state$.getValue();
      const newState: T = updaterFn(currentState, value);
      this.state$.next(newState);
    });
  }

  protected patchState(partialState: Partial<T>): void {
    const currentState: T = this.state$.getValue();
    const newState: T = { ...currentState, ...partialState };
    this.state$.next(newState);
  }

  protected updateStateProp<K extends keyof T>(prop: K, data: T[K]): void {
    this.updater((state: T, data: T[K]) => ({ ...state, [prop]: data }))(data);
  }
}
